import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

export interface RenovacaoFaixaParametro {
  id: string;
  versao_id: string;
  tabela: 1 | 2;
  faixa: number;
  delta_min: number | null;
  delta_max: number | null;
  fator_p: number;
  fator_m: number;
  fator_g: number;
}

export interface PedidoFixaRenovacao {
  id: string;
  numero: string;
  dataAtivacao: string | null;
  mesTrabalho: string | null;
  instalado: boolean;
}

type ParametroRow = { id: string; codigo: string; vigencia_inicio: string; vigencia_fim: string | null };
type ConfigRow = { chave: string; valor: unknown };

export function useRenovacaoParametros(mesCompetencia: string, cnpj?: string | null) {
  return useQuery({
    queryKey: ['renovacao-parametros', mesCompetencia, cnpj],
    queryFn: async () => {
      const inicioMes = `${mesCompetencia}-01`;
      const { data: versoes, error: erroVersoes } = await supabase
        .from('param_renovacao_versao' as never)
        .select('id, codigo, vigencia_inicio, vigencia_fim')
        .eq('ativo', true)
        .order('vigencia_inicio', { ascending: false });
      if (erroVersoes) throw erroVersoes;

      const versao = ((versoes || []) as unknown as ParametroRow[]).find((item) =>
        item.vigencia_inicio <= inicioMes && (!item.vigencia_fim || item.vigencia_fim >= inicioMes),
      );
      if (!versao) throw new Error(`Não existe tabela de renovação vigente em ${mesCompetencia}`);

      const { data: faixasData, error: erroFaixas } = await supabase
        .from('param_renovacao_faixa' as never)
        .select('id, versao_id, tabela, faixa, delta_min, delta_max, fator_p, fator_m, fator_g')
        .eq('versao_id', versao.id)
        .order('faixa', { ascending: true });
      if (erroFaixas) throw erroFaixas;

      const { data: configs, error: erroConfigs } = await supabase
        .from('configuracoes_sistema')
        .select('chave, valor')
        .in('chave', ['delta_formula_sign', 'cluster_movel_voz_vigente']);
      if (erroConfigs) throw erroConfigs;

      const configMap = new Map(((configs || []) as ConfigRow[]).map((item) => [item.chave, String(item.valor ?? '')]));
      const cnpjDigits = String(cnpj || '').replace(/\D/g, '');
      const cnpjFormatado = cnpjDigits.length === 14
        ? cnpjDigits.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/, '$1.$2.$3/$4-$5')
        : cnpjDigits;
      let pedidosFixa: PedidoFixaRenovacao[] = [];

      if (cnpjDigits) {
        const { data: itens } = await supabase
          .from('v_planilha_mae')
          .select('pedido_id, numero_pedido_completo, produto, data_ativacao, mes_trabalho, quadro_atual, pedido_created_at')
          .in('cnpj', Array.from(new Set([cnpjDigits, cnpjFormatado])))
          .in('produto', ['FIBRA', 'FIBRA MIGRA', 'FIBRA NEXT', 'FIBRA UP'])
          .neq('quadro_atual', 'PEDIDO_CANCELADO')
          .order('pedido_created_at', { ascending: false })
          .limit(50);

        const unicos = new Map<string, PedidoFixaRenovacao>();
        for (const item of itens || []) {
          if (!item.pedido_id || unicos.has(item.pedido_id)) continue;
          unicos.set(item.pedido_id, {
            id: item.pedido_id,
            numero: item.numero_pedido_completo || String(item.pedido_id).slice(0, 8),
            dataAtivacao: item.data_ativacao,
            mesTrabalho: item.mes_trabalho,
            instalado: Boolean(item.data_ativacao),
          });
        }
        pedidosFixa = [...unicos.values()];
      }

      return {
        versao,
        faixas: ((faixasData || []) as unknown as RenovacaoFaixaParametro[]),
        deltaFormulaSign: configMap.get('delta_formula_sign') === 'LITERAL_REGULAMENTO'
          ? 'LITERAL_REGULAMENTO' as const
          : 'ECONOMICO' as const,
        cluster: ['P', 'M', 'G'].includes(configMap.get('cluster_movel_voz_vigente') || '')
          ? configMap.get('cluster_movel_voz_vigente') as 'P' | 'M' | 'G'
          : 'NAO_INFORMADO' as const,
        pedidosFixa,
      };
    },
    enabled: /^\d{4}-\d{2}$/.test(mesCompetencia),
    staleTime: 60_000,
  });
}

export function useAtualizarParametroRenovacao() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ chave, valor }: { chave: 'delta_formula_sign' | 'cluster_movel_voz_vigente'; valor: string }) => {
      const { error } = await supabase
        .from('configuracoes_sistema')
        .update({ valor })
        .eq('chave', chave);
      if (error) throw error;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['renovacao-parametros'] }),
  });
}

export function useRenovacaoSnapshot(pedidoId: string, leadId?: string) {
  return useQuery({
    queryKey: ['renovacao-snapshot', pedidoId, leadId],
    queryFn: async () => {
      let query = supabase
        .from('v_renovacao_snapshot_comercial' as never)
        .select('payload, pedido_id, created_at')
        .order('created_at', { ascending: false })
        .limit(1);
      query = leadId
        ? query.or(`pedido_id.eq.${pedidoId},lead_id.eq.${leadId}`)
        : query.eq('pedido_id', pedidoId);
      const { data, error } = await query
        .maybeSingle();
      if (error) throw error;
      return (data as unknown as { payload?: unknown } | null)?.payload;
    },
    enabled: Boolean(pedidoId),
  });
}
